/* eslint-disable jsx-a11y/img-redundant-alt */
// the above is for the profile image alt text; it doesn't want the text "image," "photo", etc. in it,
// but I can't think of an alt text for it that doesn't have those things.
import React, { ChangeEvent, RefObject, useState } from 'react';

interface ProfileInfo {
  id?: string | null;
  name?: string | null;
  email?: string | null;
  profileImage?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  preferredUsername?: string | null;
  userName?: string | null;
}

interface ProfileFormProps {
  formData: ProfileInfo;
  handleInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent) => void;
  fileInputRef: RefObject<HTMLInputElement>;
  apiMessage?: string;
}

const ProfileForm: React.FC<ProfileFormProps> = ({
  formData,
  handleInputChange,
  handleFileChange,
  handleSubmit,
  fileInputRef,
  apiMessage,
}) => {
  const [firstNameError, setFirstNameError] = useState<string | null>(null);
  const [lastNameError, setLastNameError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);

  const validateForm = () => {
    let hasError = false;

    if (!formData.firstName) {
      setFirstNameError('First Name is required');
      hasError = true;
    } else {
      setFirstNameError(null);
    }

    if (!formData.lastName) {
      setLastNameError('Last Name is required');
      hasError = true;
    } else {
      setLastNameError(null);
    }

    return !hasError;
  };

  return (
    <>
      <div className="profile-h1-container">
        <h1 className="profile-h1 type-md">Profile Details</h1>
        <button
          className="btn btn-primary btn-semi-slim profile-btn-small-screen-only"
          onClick={() => {
            setEditMode(!editMode);
          }}
        >
          {editMode ? 'Cancel' : 'Edit Profile'}
        </button>
      </div>
      <div className="profile-info-container">
        <div className="profile-info">
          <div>
            <img
              src={formData?.profileImage || '/img/default-person.png'}
              className="profile-info-img"
              alt="Your profile picture"
            />
          </div>
          <div className="profile-info-text">
            <div className="type-3x profile-info-display-name">
              {formData?.preferredUsername || formData?.firstName}
            </div>
            <div className="type-lg">
              {formData?.firstName} {formData?.lastName}
            </div>
          </div>
        </div>
      </div>
      <form
        className="has-slim-inputs has-full-width-inputs"
        onSubmit={(e) => {
          e.preventDefault();
          if (validateForm()) {
            handleSubmit(e);
          }
        }}
      >
        <div className="profile-form-grid">
          <div className="input-container">
            <label>
              <div className="label-text">Update Photo</div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
              />
            </label>
          </div>
          <div></div>
          <div className="input-container">
            <label>
              <div className="label-text">First Name</div>
              <input
                type="text"
                name="firstName"
                value={formData?.firstName || (!editMode && '(none)') || ''}
                onChange={handleInputChange}
                disabled={!editMode}
                className={
                  !editMode && !formData?.firstName ? 'profile-none' : ''
                }
              />
              {firstNameError && <div>{firstNameError}</div>}
            </label>
          </div>

          <div className="input-container">
            <label>
              <div className="label-text">Last Name</div>
              <input
                type="text"
                name="lastName"
                value={formData?.lastName || (!editMode && '(none)') || ''}
                onChange={handleInputChange}
                disabled={!editMode}
                className={
                  !editMode && !formData?.lastName ? 'profile-none' : ''
                }
              />
              {lastNameError && <div>{lastNameError}</div>}
            </label>
          </div>

          <div className="input-container">
            <label>
              <div className="label-text">Display Name</div>
              <input
                type="text"
                name="preferredUsername"
                value={
                  formData?.preferredUsername || (!editMode && '(none)') || ''
                }
                onChange={handleInputChange}
                disabled={!editMode}
                className={
                  !editMode && !formData?.preferredUsername
                    ? 'profile-none'
                    : ''
                }
              />
            </label>
          </div>

          <div className="input-container">
            <label>
              <div className="label-text">
                Username <em>(can&apos;t be changed)</em>
              </div>
              <input
                type="text"
                name="userName"
                disabled={true}
                value={formData?.userName ?? ''}
                onChange={handleInputChange}
              />
            </label>
          </div>

          <div className="input-container">
            <label>
              <div className="label-text">
                Email <em>(can&apos;t be changed)</em>
              </div>
              <input
                type="text"
                name="email"
                disabled={true}
                value={formData?.email ?? ''}
                onChange={handleInputChange}
              />
            </label>
          </div>
        </div>

        {apiMessage && <div>{apiMessage}</div>}
        <div className="profile-form-submit-container">
          <button
            className="btn btn-primary btn-semi-slim profile-btn-large-screen-only"
            onClick={(e) => {
              e.preventDefault();
              setEditMode(!editMode);
            }}
          >
            {editMode ? 'Cancel' : 'Edit Profile'}
          </button>
          <button
            onClick={() => {
              setEditMode(!editMode);
            }}
            style={editMode ? { display: 'block' } : { display: 'none' }}
            type="submit"
            className="btn btn-primary btn-semi-slim"
          >
            Save
          </button>
        </div>
      </form>
    </>
  );
};

export default ProfileForm;
